import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "report", "validateBtn", "applyBtn", "modal"]

  connect() {
    this.modalTarget?.addEventListener('hidden.bs.modal', () => this.resetModal())
  }

  disconnect() {
    this.modalTarget?.removeEventListener('hidden.bs.modal', () => this.resetModal())
  }

  resetModal() {
    this.formTarget?.reset()
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')
  }

  onFileChange() {
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
  }

  // Utility methods
  resetButton(button, text) {
    if (!button) return
    button.disabled = false
    button.textContent = text
  }

  setButtonLoading(button, text) {
    if (!button) return
    button.disabled = true
    button.textContent = text
  }

  scrollableDiv(content) {
    return `<div style="max-height: 300px; overflow-y: auto; margin-top: 10px;">${content}</div>`
  }

  formatRowError(rowError) {
    return rowError.row
      ? `Row ${rowError.row}: ${rowError.errors.join('; ')}`
      : rowError.errors.join('; ')
  }

  formatErrorsList(errors_by_row) {
    return errors_by_row.map(rowError => this.formatRowError(rowError)).join('<br>')
  }

  formatFieldValues(field_values) {
    return Object.entries(field_values || {})
      .map(([field, value]) => `${field}: ${value}`)
      .join('; ')
  }

  section(title, count, content) {
    return `${title} (${count})\n${'-'.repeat(70)}\n${content}\n`
  }

  separator() {
    return '='.repeat(70)
  }

  validate(e) {
    e.preventDefault()

    if (!this.fileInputTarget.files.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls)$/)) {
      alert('Please select a valid XLSX or XLS file')
      return
    }

    this.onFileChange()
    this.setButtonLoading(this.validateBtnTarget, 'Validating...')

    fetch(this.formTarget.action, {
      method: 'POST',
      body: new FormData(this.formTarget),
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
    })
    .then(response => this.parseJson(response))
    .then(({ response, data }) => {
      if (response.ok) {
        this.displayReport(data)
      } else {
        this.displayUploadError(data.errors || ['Validation failed. Please try again.'])
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Validation failed. Please try again.')
    })
    .finally(() => this.resetButton(this.validateBtnTarget, 'Validate'))
  }

  displayReport(data) {
    const errorsList = data.errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row))}
        </div>`
      : ''

    const readyMessage = data.errors_count === 0 && data.to_update > 0
      ? '<div class="alert alert-info">Validation passed! Ready to import.</div>'
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert ${data.errors_count === 0 ? 'alert-success' : 'alert-warning'} mb-3">
        <strong>Summary:</strong><br>
        To Update: ${data.to_update}
      </div>
      ${errorsList}
      ${readyMessage}
    `

    this.applyBtnTarget?.classList[data.to_update > 0 ? 'remove' : 'add']('d-none')
  }

  apply(e) {
    e.preventDefault()

    if (!confirm('Are you sure you want to apply this import?')) return

    this.setButtonLoading(this.applyBtnTarget, 'Importing...')

    fetch('/admin/publishers_bulk/apply_import', {
      method: 'POST',
      body: new FormData(this.formTarget),
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => this.parseJson(response))
    .then(({ response, data }) => {
      if (response.ok) {
        data.success ? this.displayImportResults(data) : this.displayImportErrors(data)
      } else {
        this.displayUploadError(data.errors || [data.message || 'Import failed.'])
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('Import failed. Please try again.')
    })
    .finally(() => this.resetButton(this.applyBtnTarget, 'Apply Import'))
  }

  displayImportResults(data) {
    const { summary, successes, failures, errors_by_row } = data
    this.lastImportData = data

    const updated = successes?.filter(s => s.action === 'updated') || []

    const updatedSection = updated.length > 0
      ? `<div class="alert alert-info">
          <strong>Updated:</strong>
          ${this.scrollableDiv(updated.map(item =>
            `Row ${item.row}: '${item.name}' - ${this.formatFieldValues(item.field_values)}`
          ).join('<br>'))}
        </div>`
      : ''

    const failedSection = failures?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Failed:</strong>
          ${this.scrollableDiv(failures.map(item =>
            `Row ${item.row}: '${item.key}' - ${item.error}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorsSection = errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(errors_by_row))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-success mb-3">
        <strong>Import Completed!</strong><br>
        Updated: ${summary.updated}; Failed: ${summary.failed}
      </div>
      ${updatedSection}
      ${failedSection}
      ${errorsSection}
      <div class="text-center mt-3">
        <button type="button" class="btn btn-success" data-action="click->import#downloadReport">
          <i class="bi bi-download me-2"></i>
          Download Report
        </button>
      </div>
    `

    this.applyBtnTarget?.classList.add('d-none')
    this.validateBtnTarget?.classList.remove('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')
  }

  displayImportErrors(data) {
    const errorsSection = data.errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong>Import Failed</strong><br>
        ${data.message}
      </div>
      ${errorsSection}
    `
  }

  downloadReport(e) {
    e.preventDefault()

    if (!this.lastImportData) {
      alert('No import data available')
      return
    }

    const reportText = this.generateTextReport(this.lastImportData)
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `publishers_import_report_${new Date().toISOString().slice(0, 10)}.txt`
    link.click()
    URL.revokeObjectURL(url)
  }

  generateTextReport(data) {
    const { summary, successes, failures, errors_by_row } = data
    const lines = [
      this.separator(),
      'PUBLISHERS IMPORT REPORT',
      this.separator(),
      `Date: ${new Date().toLocaleString()}`,
      'Status: Success',
      '',
      'SUMMARY',
      '-'.repeat(70),
      `Total Updated: ${summary.updated}`,
      `Total Failed: ${summary.failed}`,
      ''
    ]

    if (successes?.length > 0) {
      lines.push(
        ...this.section('UPDATED PUBLISHERS', successes.length,
          successes.map(item =>
            `Row ${item.row}: '${item.name}' - ${this.formatFieldValues(item.field_values)}`
          ).join('\n')
        ).split('\n')
      )
    }

    if (failures?.length > 0) {
      lines.push(
        ...this.section('FAILED PUBLISHERS', failures.length,
          failures.map(item => `Row ${item.row}: '${item.key}' - ${item.error}`).join('\n')
        ).split('\n')
      )
    }

    if (errors_by_row?.length > 0) {
      lines.push(
        ...this.section('VALIDATION ERRORS', `${errors_by_row.length} rows`,
          errors_by_row.map(rowError => this.formatRowError(rowError)).join('\n')
        ).split('\n')
      )
    }

    lines.push(this.separator(), 'END OF REPORT', this.separator())

    return lines.join('\n')
  }

  parseJson(response) {
    return response.json()
      .then(data => ({ response, data }))
      .catch(() => ({ response, data: {} }))
  }

  displayUploadError(errors) {
    if (!this.reportTarget) return

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        ${errors.join('<br>')}
      </div>
    `

    this.applyBtnTarget?.classList.add('d-none')
  }
}
